// src/components/auth/LoginSuccess.jsx
import React, { useEffect, useState } from 'react';
import {
    Typography,
    Container,
    Box,
    Button,
    Card,
    CardContent,
    Alert,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getCurrentUser, getProfil } from '../../api';
import { useAuth } from './AuthContext';
import { isKeycloakMode } from '../../auth/keycloakClient';
import { hasActiveSubscriptionAccess } from '../../subscription';

export default function LoginSuccess() {
    const navigate = useNavigate();
    const [profileExists, setProfileExists] = useState(null);
    const { isAuthenticated, user, updateUser } = useAuth();
    const [keycloakError, setKeycloakError] = useState(null);

    useEffect(() => {
        // If Keycloak redirected back with an error (e.g. PKCE/redirect_uri), don't start API calls
        // and don't auto-redirect back to /login, otherwise we get loops.
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search || '');
            const error = params.get('error');
            const errorDescription = params.get('error_description');
            if (error) {
                setKeycloakError({
                    error,
                    description: errorDescription || '',
                });
                return undefined;
            }
        }

        // In Keycloak mode, wait until the adapter processed the auth callback
        // and the API client can attach a bearer token.
        if (isKeycloakMode() && !isAuthenticated) {
            return undefined;
        }

        let cancelled = false;
        let timer;

        const determineRedirect = async () => {
            let currentUser = user ?? null;
            if (!currentUser) {
                try {
                    const me = await getCurrentUser();
                    if (cancelled) {
                        return;
                    }
                    currentUser = me?.data ?? null;
                    if (currentUser) {
                        updateUser(currentUser, { remember: true });
                    }
                } catch (error) {
                    if (cancelled) {
                        return;
                    }
                }
            }

            if (!hasActiveSubscriptionAccess(currentUser)) {
                if (!cancelled) {
                    setProfileExists(false);
                    timer = setTimeout(() => {
                        navigate('/account?checkout=required', { replace: true });
                    }, 1500);
                }
                return;
            }

            let hasProfile = false;
            try {
                const res = await getProfil();
                if (cancelled) {
                    return;
                }
                hasProfile = Boolean(res?.data?.id);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                hasProfile = false;
            }

            if (!cancelled) {
                setProfileExists(hasProfile);
                const target = hasProfile ? '/dashboard' : '/profile';
                timer = setTimeout(() => {
                    navigate(target);
                }, 3000);
            }
        };

        determineRedirect();

        return () => {
            cancelled = true;
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [isAuthenticated, navigate, updateUser, user]);

    if (keycloakError) {
        const isPkceMissing = (keycloakError.description || '').toLowerCase().includes('code_challenge_method');
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                        'linear-gradient(135deg, rgba(255,165,0,0.08) 0%, rgba(0,0,0,0.02) 100%)',
                    py: 6,
                }}
            >
                <Container maxWidth="sm">
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                            p: { xs: 3, md: 4 },
                        }}
                    >
                        <CardContent>
                            <Typography variant="h4" fontWeight={700} gutterBottom>
                                Anmeldung fehlgeschlagen
                            </Typography>

                            <Alert severity="error" sx={{ mb: 2 }}>
                                Keycloak hat die Anmeldung abgelehnt: <strong>{keycloakError.error}</strong>
                                {keycloakError.description ? ` (${keycloakError.description})` : ''}
                            </Alert>

                            {isPkceMissing && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    Dein Keycloak-Client scheint PKCE zu erzwingen, aber dein Frontend läuft über HTTP
                                    auf einer IP-Adresse. In diesem Kontext ist die WebCrypto-API (für PKCE S256) oft
                                    nicht verfügbar. Lösung: Frontend über HTTPS betreiben oder im Keycloak-Client PKCE
                                    für dieses Client-ID deaktivieren.
                                </Alert>
                            )}

                            <Button
                                component={RouterLink}
                                to="/login?reason=keycloak_error"
                                variant="contained"
                                sx={{
                                    mt: 1,
                                    backgroundColor: 'orange',
                                    '&:hover': { backgroundColor: '#cc7a00' },
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.2,
                                    fontWeight: 600,
                                }}
                            >
                                Zurück zum Login
                            </Button>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        );
    }

    const needsCheckout = user && !hasActiveSubscriptionAccess(user);
    const targetPath = needsCheckout ? '/account?checkout=required' : profileExists === false ? '/profile' : '/dashboard';
    const targetLabel = needsCheckout ? 'Tarifauswahl' : profileExists === false ? 'Profil' : 'Dashboard';
    const loadingProfile = profileExists === null;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background:
                    'linear-gradient(135deg, rgba(255,165,0,0.08) 0%, rgba(0,0,0,0.02) 100%)',
                py: 6,
            }}
        >
            <Container maxWidth="sm">
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 4,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                        textAlign: 'center',
                        p: { xs: 3, md: 4 },
                    }}
                >
                    <CardContent>
                        <CheckCircleOutlineIcon
                            sx={{ fontSize: 72, color: 'green', mb: 2 }}
                        />

                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            Erfolgreich eingeloggt
                        </Typography>

                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            Willkommen zurück bei <strong>Schreiberlein</strong>! <br />
                            {loadingProfile
                                ? 'Wir prüfen dein Profil und leiten dich gleich weiter.'
                                : `Du wirst gleich automatisch zum ${targetLabel} weitergeleitet.`}
                        </Typography>

                        <Button
                            component={RouterLink}
                            to={targetPath}
                            variant="contained"
                            disabled={loadingProfile}
                            sx={{
                                mt: 3,
                                backgroundColor: 'orange',
                                '&:hover': { backgroundColor: '#cc7a00' },
                                borderRadius: 2,
                                px: 4,
                                py: 1.2,
                                fontWeight: 600,
                            }}
                        >
                            {loadingProfile ? 'Weiterleitung vorbereitet …' : `Direkt zum ${targetLabel}`}
                        </Button>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 2 }}
                        >
                            Automatische Weiterleitung in wenigen Sekunden …
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}
